import { mixed, number, object, string } from 'yup';

import {
	EAllowToRenotify,
	ENotify,
} from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule/AddKpiAlertSchedule/AddKpiAlertSchedule';

import { getSchema } from '@utils/schemaUtils';

import { TValueOfStringRecord } from '@interfaces/common/genericWithModifying';

import { EScheduleFields } from './enums';

import { getMinMaxDigitalValue, mandatoryErrMessage } from '@constants/validationConstants';

export const MAX_NUMBER_OF_REPETITIONS = 100;
export const MIN_NUMBER_OF_REPETITIONS = 2;

export const EScheduleErrors = {
	allowToRenotify: 'The value in ‘Allow to renotify’ must be between 1 minute and 23h 59 min',
} as const;

export type TScheduleErrorsEnum = TValueOfStringRecord<typeof EScheduleErrors>

export const scheduleFields = {
	notify: string()
		.required(mandatoryErrMessage('Notify')),
	numberOfRepetitions: mixed().when(EScheduleFields.notify, {
		is: (notify: string) => {
			return notify === ENotify.customValue;
		},
		then: number()
			.required(mandatoryErrMessage('Values are outside the threshold'))
			.nullable()
			.min(
				MIN_NUMBER_OF_REPETITIONS,
				getMinMaxDigitalValue({ min: MIN_NUMBER_OF_REPETITIONS, max: MAX_NUMBER_OF_REPETITIONS }),
			)
			.max(
				MAX_NUMBER_OF_REPETITIONS,
				getMinMaxDigitalValue({ min: MIN_NUMBER_OF_REPETITIONS, max: MAX_NUMBER_OF_REPETITIONS }),
			),
		otherwise: number().nullable().notRequired(),
	}),
	allowToRenotify: string().required(mandatoryErrMessage('Allow to renotify')),
	allowToRenotifyAfterCustomValueMinutes: mixed().nullable().when(EScheduleFields.allowToRenotify, {
		is: (allowToRenotify: string) => {
			return allowToRenotify === EAllowToRenotify.customValue;
		},
		then: object().nullable().required(mandatoryErrMessage('Allow to renotify')),
		otherwise: object().nullable().notRequired(),
	}).test(
		EScheduleFields.allowToRenotifyAfterCustomValueMinutes,
		EScheduleErrors.allowToRenotify,
		(value, context) => {
			const { allowToRenotifyAfterCustomValueHours } = context.parent;

			return !(allowToRenotifyAfterCustomValueHours?.value === 0 && value?.value === 0);
		},
	),
	allowToRenotifyAfterCustomValueHours: mixed().nullable().when('allowToRenotify', {
		is: (allowToRenotify: string) => {
			return allowToRenotify === EAllowToRenotify.customValue;
		},
		then: object().nullable().required(mandatoryErrMessage('Allow to renotify')),
		otherwise: object().nullable().notRequired(),
	}).test(
		EScheduleFields.allowToRenotifyAfterCustomValueHours,
		EScheduleErrors.allowToRenotify,
		(value, context) => {
			const { allowToRenotifyAfterCustomValueMinutes } = context.parent;

			return !(allowToRenotifyAfterCustomValueMinutes?.value === 0 && value?.value === 0);
		}),
};

export const scheduleSchema = getSchema(scheduleFields);
